// Errors are metav1.Status with reason, fix, docs and requestId (ADR-028). One handler routes money errors to their
// single call to action (ADR-107, ui-spec §4.5); the inference data plane's OpenAI/Anthropic error shape is adapted
// into the same Status first, so the playground and endpoint pages route exactly like the control plane.
import { linkTo } from "./linkto";
import { isStatus, type Status } from "./types";

export class ApiError extends Error {
  readonly code: number;
  readonly status: Status;
  readonly requestId?: string;
  readonly retryAfterSeconds?: number;

  constructor(code: number, status: Status, requestId?: string, retryAfterSeconds?: number) {
    super(status.message ?? `Request failed with ${code}`);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    const rid = requestId ?? status.requestId;
    if (rid !== undefined) this.requestId = rid;
    const ra = retryAfterSeconds ?? status.details?.retryAfterSeconds;
    if (ra !== undefined) this.retryAfterSeconds = ra;
  }

  get reason(): string | undefined {
    return this.status.reason;
  }
}

/** A transport failure before response headers or while reading a streamed response body. */
export class NetworkError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = "NetworkError";
  }
}

/** Builds a Status from any error body: metav1.Status, the inference error shape, or plain text. */
export function toStatus(code: number, body: unknown): Status {
  if (isStatus(body)) return body;
  const spot = body as { error?: unknown; retry_after?: number; current_price?: number; message?: string } | undefined;
  if (typeof spot?.error === "string") {
    const messages: Record<string, string> = {
      no_capacity: "No matching Spot capacity is available. Try again in 30 seconds.",
      price_above_max: "The current Spot price exceeds your maximum price.",
      payment_required: "Review your billing status, available credits and spending limits before launching.",
      node_limit: "Your active Spot node limit has been reached. Review your compute limits or release a node.",
      provisioning_limit: "Your limit for nodes starting at the same time has been reached.",
      storage_quota: "Your retained-storage limit has been reached. Review your storage limit or release unused storage.",
    };
    return {
      kind: "Status", status: "Failure", code, reason: spot.error,
      message: messages[spot.error] ?? spot.message ?? `Request failed (${code})`,
      ...(spot.retry_after ? { details: { retryAfterSeconds: spot.retry_after } } : {}),
    };
  }
  const inference = (body as { error?: { type?: string; code?: string; message?: string } } | undefined)?.error;
  if (inference && typeof inference === "object") {
    return {
      kind: "Status",
      status: "Failure",
      code,
      ...(inference.code || inference.type ? { reason: inference.code ?? inference.type } : {}),
      ...(inference.message ? { message: inference.message } : {}),
    };
  }
  return { kind: "Status", status: "Failure", code, message: typeof body === "string" && body ? body : `HTTP ${code}` };
}

export interface MoneyAction {
  message: string;
  label: string;
  href: string;
}

/** One call to action per money error code (ui-spec §4.5); undefined for anything that is not a money error. */
export function moneyAction(err: ApiError, scope: { org: string; project?: string }): MoneyAction | undefined {
  const reason = err.reason;
  const message = err.status.message ?? "This needs more funds.";
  const detailKind = err.status.details?.kind;
  const detailName = err.status.details?.name;
  const billing = linkTo({ kind: "BillingAccount" }, { org: scope.org });
  const configure = (kind: string, name: string, field: string) =>
    linkTo({ kind, name }, { ...scope, tab: "configuration", query: { focus: field } });
  switch (reason) {
    case "payment_required":
      return { message, label: "Review billing", href: billing };
    case "PaymentMethodRequired":
      return { message, label: "Add a card", href: billing };
    case "InsufficientCredits":
    case "ArrearsOutstanding":
      return { message, label: "Add credits", href: billing };
    case "BudgetExceeded":
      if (detailKind === "Budget" && detailName) {
        return { message, label: "Raise budget", href: linkTo({ kind: "Budget", name: detailName }, { org: scope.org, query: { focus: "spec.limitUSD" } }) };
      }
      if (detailKind === "Agent" && detailName) {
        return { message, label: "Raise per-run limit", href: configure("Agent", detailName, "spec.perRunMaxCostUSD") };
      }
      if (detailKind && detailName) return { message, label: "Raise limit", href: configure(detailKind, detailName, "spec.maxCostUSD") };
      return { message, label: "Open budgets", href: linkTo({ kind: "Budget" }, { org: scope.org }) };
    case "MaxCostReached":
      if (detailKind === "Agent" && detailName) {
        return { message, label: "Raise per-run limit", href: configure("Agent", detailName, "spec.perRunMaxCostUSD") };
      }
      if (detailKind && detailName) return { message, label: "Raise limit", href: configure(detailKind, detailName, "spec.maxCostUSD") };
      return undefined;
    case "PaymentDisputed":
      return { message, label: "Contact support", href: `/${encodeURIComponent(scope.org)}/support?type=Dispute` };
    default:
      return undefined;
  }
}

export type ErrorClass =
  | "money"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "preconditionFailed"
  | "invalid"
  | "quota"
  | "rateLimited"
  | "unauthorized"
  | "uncertain"
  | "other";

/** How the UI treats an error (ui-spec §4.6, §4.17). `mutation` marks creates and state changes. */
export function classify(err: unknown, mutation = false): ErrorClass {
  if (err instanceof NetworkError) return mutation ? "uncertain" : "other";
  if (!(err instanceof ApiError)) return "other";
  if (err.code === 402) return "money";
  if (err.code === 401) return "unauthorized";
  if (err.code === 403) return "forbidden";
  if (err.code === 404) return "notFound";
  if (err.code === 409) return "conflict";
  if (err.code === 412) return "preconditionFailed";
  if (err.code === 422) return "invalid";
  if (err.code === 429) return ["QuotaExceeded", "node_limit", "provisioning_limit", "storage_quota"].includes(err.reason ?? "") ? "quota" : "rateLimited";
  if (err.code >= 500) return mutation ? "uncertain" : "other";
  return "other";
}

/** Reads may retry after `Retry-After` on rate limits; quota errors and mutations never retry automatically. */
export function shouldRetry(err: unknown, attempt: number): boolean {
  if (attempt >= 3) return false;
  if (err instanceof NetworkError) return true;
  if (!(err instanceof ApiError)) return false;
  return (err.code === 429 && !["QuotaExceeded", "node_limit", "provisioning_limit", "storage_quota"].includes(err.reason ?? "")) || err.code >= 500;
}

function sentence(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The ErrorState view model of @nodus/ui. */
export function errorView(err: unknown): {
  title: string;
  reason?: string;
  message?: string;
  fix?: string;
  docsHref?: string;
  requestId?: string;
} {
  if (err instanceof ApiError) {
    // A 503 with its own reason names an outside cause, such as the customer's cloud not answering.
    const outside = err.code === 503 && err.reason !== undefined && err.reason !== "Unavailable" && Boolean(err.status.message);
    return {
      title: outside ? sentence(err.status.message ?? "")
        : err.code >= 500 ? "Something went wrong on our side" : (err.status.message ?? `Request failed (${err.code})`),
      ...(err.reason ? { reason: err.reason } : {}),
      ...(err.code >= 500 && !outside && err.status.message ? { message: err.status.message } : {}),
      ...(err.status.fix ? { fix: err.status.fix } : {}),
      ...(err.status.docs ? { docsHref: err.status.docs } : {}),
      ...(err.requestId ? { requestId: err.requestId } : {}),
    };
  }
  if (err instanceof NetworkError) return { title: "Can't reach Nodus", message: "Check your connection and retry." };
  return { title: "Something went wrong", message: err instanceof Error ? err.message : String(err) };
}
